import { apiClient } from "./client";
import {
  LoginRequest,
  LoginResponse,
  User,
  LPRSession,
  OrphanSession,
  Camera,
  DashboardKPIs,
  StateAnalytics,
  CameraPerformance,
  TimeSeriesData,
  ProblemPlate,
  ProblemPlateExclusion,
  CSVUploadResponse,
  UploadStatusResponse,
  SessionQueryParams,
  CameraQueryParams,
  AnalyticsQueryParams,
  ZoneOrphanQueryParams,
  ProblemPlateExclusionPayload,
  APIResponse,
  PaginationInfo,
  ZoneOrphanTimeseriesResponse,
  ZoneOrphanDowResponse,
  Insight,
  InsightSummary,
  InsightQueryParams,
  GenerateInsightsRequest,
} from "@/types";

// Authentication endpoints
export const authAPI = {
  login: (credentials: LoginRequest): Promise<APIResponse<LoginResponse>> => {
    return apiClient.post("/api/auth/login/json", credentials);
  },

  logout: (): Promise<APIResponse<void>> => {
    return apiClient.post("/api/auth/logout");
  },

  refreshToken: (refreshToken: string): Promise<APIResponse<LoginResponse>> => {
    return apiClient.post("/api/auth/refresh", { refresh_token: refreshToken });
  },

  getCurrentUser: (): Promise<APIResponse<User>> => {
    return apiClient.get("/api/auth/me");
  },

  changePassword: (data: {
    currentPassword: string;
    newPassword: string;
  }): Promise<APIResponse<void>> => {
    return apiClient.post("/api/auth/change-password", data);
  },
};

// Session endpoints
export const sessionAPI = {
  getSessions: (
    params?: SessionQueryParams,
  ): Promise<APIResponse<LPRSession[]>> => {
    return apiClient.get("/api/sessions/", params);
  },

  getSession: (id: string): Promise<APIResponse<LPRSession>> => {
    return apiClient.get(`/api/sessions/${id}`);
  },

  updateSession: (
    id: string,
    data: Partial<LPRSession>,
  ): Promise<APIResponse<LPRSession>> => {
    return apiClient.put(`/api/sessions/${id}`, data);
  },

  deleteSession: (id: string): Promise<APIResponse<void>> => {
    return apiClient.delete(`/api/sessions/${id}`);
  },

  exportSessions: (
    params?: SessionQueryParams,
    format: "csv" | "xlsx" = "csv",
  ): Promise<void> => {
    return apiClient.download(
      `/api/sessions/export/csv?format=${format}`,
      `sessions.${format}`,
    );
  },

  getSessionImages: (
    sessionId: string,
  ): Promise<APIResponse<{ images: any[] }>> => {
    return apiClient.get(`/api/sessions/${sessionId}/images`);
  },

  bulkUpdateSessions: (
    sessionIds: string[],
    data: Partial<LPRSession>,
  ): Promise<APIResponse<void>> => {
    return apiClient.post("/api/sessions/bulk-update", {
      session_ids: sessionIds,
      ...data,
    });
  },
};

// Orphan session endpoints
export const orphanAPI = {
  getOrphanEntries: (
    params?: SessionQueryParams,
  ): Promise<APIResponse<OrphanSession[]>> => {
    return apiClient.get("/api/orphans/entries", params);
  },

  getOrphanExits: (
    params?: SessionQueryParams,
  ): Promise<APIResponse<OrphanSession[]>> => {
    return apiClient.get("/api/orphans/exits", params);
  },

  getOrphans: (
    params?: SessionQueryParams,
  ): Promise<APIResponse<OrphanSession[]>> => {
    return apiClient.get("/api/orphans/entries", params);
  },

  getOrphan: (id: string): Promise<APIResponse<OrphanSession>> => {
    return apiClient.get(`/api/orphans/entries/${id}`);
  },

  getZoneTimeseries: (
    params?: ZoneOrphanQueryParams,
  ): Promise<APIResponse<ZoneOrphanTimeseriesResponse>> => {
    return apiClient.get("/api/orphans/zones/timeseries", params);
  },

  getZoneDayOfWeek: (
    params?: ZoneOrphanQueryParams,
  ): Promise<APIResponse<ZoneOrphanDowResponse>> => {
    return apiClient.get("/api/orphans/zones/day-of-week", params);
  },

  linkOrphan: (
    orphanId: string,
    sessionId: string,
  ): Promise<APIResponse<LPRSession>> => {
    return apiClient.post(`/api/orphans/match/${orphanId}`, {
      session_id: sessionId,
    });
  },

  dismissOrphan: (
    orphanId: string,
    reason?: string,
  ): Promise<APIResponse<void>> => {
    return apiClient.delete(`/api/orphans/dismiss/${orphanId}`, { reason });
  },

  bulkDismissOrphans: (
    orphanIds: string[],
    reason?: string,
  ): Promise<APIResponse<void>> => {
    return apiClient.post("/api/orphans/bulk-dismiss", {
      orphan_ids: orphanIds,
      reason,
    });
  },

  findMatches: (
    orphanId: string,
  ): Promise<APIResponse<{ matches: LPRSession[] }>> => {
    return apiClient.get(`/api/orphans/suggestions/${orphanId}`);
  },
};

// Camera endpoints
export const cameraAPI = {
  getCameras: (
    params?: CameraQueryParams,
  ): Promise<
    APIResponse<{ cameras: Camera[]; pagination: PaginationInfo }>
  > => {
    return apiClient.get("/api/cameras", params);
  },

  getCamera: (id: string): Promise<APIResponse<Camera>> => {
    return apiClient.get(`/api/cameras/${id}`);
  },

  updateCamera: (
    id: string,
    data: Partial<Camera>,
  ): Promise<APIResponse<Camera>> => {
    return apiClient.put(`/api/cameras/${id}`, data);
  },

  createCamera: (data: Omit<Camera, "id">): Promise<APIResponse<Camera>> => {
    return apiClient.post("/api/cameras", data);
  },

  deleteCamera: (id: string): Promise<APIResponse<void>> => {
    return apiClient.delete(`/api/cameras/${id}`);
  },

  testCamera: (
    id: string,
  ): Promise<APIResponse<{ status: string; message: string }>> => {
    return apiClient.post(`/api/cameras/${id}/test`);
  },

  getCameraStatus: (
    id: string,
  ): Promise<APIResponse<{ status: string; last_seen: string }>> => {
    return apiClient.get(`/api/cameras/${id}/status`);
  },

  rebootCamera: (id: string): Promise<APIResponse<void>> => {
    return apiClient.post(`/api/cameras/${id}/reboot`);
  },
};

// Analytics endpoints
export const analyticsAPI = {
  getDashboardKPIs: (
    params: AnalyticsQueryParams,
  ): Promise<APIResponse<DashboardKPIs>> => {
    return apiClient.get("/api/metrics/dashboard", params);
  },

  getTimeSeriesData: (
    params: AnalyticsQueryParams & { metric: string },
  ): Promise<APIResponse<{ data: TimeSeriesData[] }>> => {
    return apiClient.get("/api/metrics/daily", params);
  },

  getStateAnalytics: (
    params: AnalyticsQueryParams,
  ): Promise<APIResponse<{ data: StateAnalytics[] }>> => {
    return apiClient.get("/api/metrics/state", params);
  },

  getCameraPerformance: (
    params: AnalyticsQueryParams,
  ): Promise<APIResponse<{ data: CameraPerformance[] }>> => {
    return apiClient.get("/api/metrics/camera", params);
  },

  getPerformanceTrends: (
    params: AnalyticsQueryParams,
  ): Promise<
    APIResponse<{
      all_locations: Array<{
        date: string;
        match_rate: number;
        exact_rate: number;
        fuzzy_rate: number;
        orphan_rate: number;
        total_reads: number;
        total_sessions: number;
        exact_matches: number;
        fuzzy_matches: number;
      }>;
      by_location: Record<string, any[]>;
    }>
  > => {
    return apiClient.get("/api/metrics/trends", params);
  },

  getProblemPlates: (
    params: AnalyticsQueryParams & { min_occurrences?: number; limit?: number },
  ): Promise<APIResponse<ProblemPlate[]>> => {
    return apiClient.get("/api/metrics/problem-plates", params);
  },

  getProblemPlateDetails: (
    plate: string,
    params: AnalyticsQueryParams,
  ): Promise<
    APIResponse<{
      plate: string;
      total_orphans: number;
      entries: Array<{
        id: number;
        plate: string;
        timestamp: string;
        camera_id: string;
        confidence: number | null;
        state: string;
        location: string;
        zone: string;
        hours_orphaned: number;
        type: "entry" | "exit";
      }>;
      exits: Array<{
        id: number;
        plate: string;
        timestamp: string;
        camera_id: string;
        confidence: number | null;
        state: string;
        location: string;
        zone: string;
        hours_orphaned: number;
        type: "entry" | "exit";
      }>;
    }>
  > => {
    return apiClient.get(
      `/api/metrics/problem-plates/${plate}/details`,
      params,
    );
  },

  getHourlyTrends: (
    params: AnalyticsQueryParams,
  ): Promise<APIResponse<{ data: TimeSeriesData[] }>> => {
    return apiClient.get("/api/metrics/daily", params);
  },

  getDailyTrends: (
    params: AnalyticsQueryParams,
  ): Promise<APIResponse<{ data: TimeSeriesData[] }>> => {
    return apiClient.get("/api/metrics/daily", params);
  },

  getProblemPlateExclusions: (): Promise<
    APIResponse<ProblemPlateExclusion[]>
  > => {
    return apiClient.get("/api/metrics/problem-plates/exclusions");
  },

  addProblemPlateExclusion: (
    payload: ProblemPlateExclusionPayload,
  ): Promise<APIResponse<ProblemPlateExclusion>> => {
    return apiClient.post("/api/metrics/problem-plates/exclusions", payload);
  },

  removeProblemPlateExclusion: (
    plate: string,
  ): Promise<APIResponse<{ plate: string }>> => {
    return apiClient.delete(`/api/metrics/problem-plates/exclusions/${plate}`);
  },

  getViolationTrends: (
    params: AnalyticsQueryParams,
  ): Promise<APIResponse<{ data: TimeSeriesData[] }>> => {
    return apiClient.get("/api/metrics/summary", params);
  },

  getTopLicensePlates: (
    params: AnalyticsQueryParams & { limit?: number },
  ): Promise<
    APIResponse<{ data: Array<{ license_plate: string; count: number }> }>
  > => {
    return apiClient.get("/api/metrics/summary", params);
  },

  getLocationAnalytics: (
    params: AnalyticsQueryParams,
  ): Promise<
    APIResponse<{
      data: Array<{ location: string; sessions: number; revenue: number }>;
    }>
  > => {
    return apiClient.get("/api/metrics/summary", params);
  },

  getMisreadMetrics: (
    params: AnalyticsQueryParams & { camera?: string; rule_type?: string },
  ): Promise<
    APIResponse<{
      summary: {
        total_reads: number;
        total_misreads: number;
        overall_rate: number;
        date_range: { start: string; end: string };
      };
      daily: Array<{
        date: string;
        total_reads: number;
        misread_count: number;
        misread_rate: number;
      }>;
      by_location: Array<{
        location_id: string;
        total_reads: number;
        misread_count: number;
        misread_rate: number;
        cameras?: Array<{
          camera_id: string;
          total_reads: number;
          misread_count: number;
          misread_rate: number;
          daily?: Array<{
            date: string;
            total_reads: number;
            misread_count: number;
            misread_rate: number;
          }>;
        }>;
        daily?: Array<{
          date: string;
          total_reads: number;
          misread_count: number;
          misread_rate: number;
        }>;
      }>;
      by_camera: Array<{
        camera_id: string;
        location_id: string;
        total_reads: number;
        misread_count: number;
        misread_rate: number;
      }>;
      available_cameras: Array<{ camera_id: string; location_id: string }>;
    }>
  > => {
    return apiClient.get("/api/metrics/misreads", params);
  },
};

// Upload endpoints
export const uploadAPI = {
  uploadCSV: (
    file: File,
    onProgress?: (progress: number) => void,
  ): Promise<APIResponse<CSVUploadResponse>> => {
    return apiClient.upload("/api/upload/csv", file, onProgress);
  },

  getUploadStatus: (
    batchId: string,
  ): Promise<APIResponse<UploadStatusResponse>> => {
    return apiClient.get(`/api/upload/status/${batchId}`);
  },

  uploadImages: (
    files: File[],
    onProgress?: (progress: number) => void,
  ): Promise<APIResponse<{ uploaded: number; failed: number }>> => {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));

    return apiClient.request({
      method: "POST",
      url: "/api/upload/images",
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          onProgress(progress);
        }
      },
    });
  },

  getUploadHistory: (): Promise<
    APIResponse<{
      uploads: Array<{
        id: string;
        filename: string;
        status: string;
        created_at: string;
        rows_processed: number;
        errors: number;
      }>;
    }>
  > => {
    return apiClient.get("/api/upload/history");
  },

  getUploadErrors: (
    uploadId: string,
  ): Promise<
    APIResponse<{
      errors: Array<{
        row: number;
        column: string;
        message: string;
        value: string;
      }>;
    }>
  > => {
    return apiClient.get(`/api/upload/${uploadId}/errors`);
  },
};

// System endpoints
export const systemAPI = {
  getSystemInfo: (): Promise<
    APIResponse<{
      version: string;
      uptime: number;
      memory_usage: number;
      cpu_usage: number;
      disk_usage: number;
    }>
  > => {
    return apiClient.get("/api/system/info");
  },

  getHealth: (): Promise<
    APIResponse<{
      status: "healthy" | "unhealthy";
      checks: Record<string, { status: string; message?: string }>;
    }>
  > => {
    return apiClient.get("/health");
  },

  getLogs: (params: {
    level?: "debug" | "info" | "warning" | "error";
    limit?: number;
    offset?: number;
  }): Promise<
    APIResponse<{
      logs: Array<{
        timestamp: string;
        level: string;
        message: string;
        context?: Record<string, any>;
      }>;
    }>
  > => {
    return apiClient.get("/api/system/logs", params);
  },

  exportLogs: (params: {
    date_from: string;
    date_to: string;
    level?: string;
  }): Promise<void> => {
    return apiClient.download("/api/system/logs/export", "system_logs.txt");
  },
};

// Reports endpoints
export const reportsAPI = {
  generateReport: (params: {
    type: "sessions" | "violations" | "cameras" | "revenue";
    date_from: string;
    date_to: string;
    format: "pdf" | "xlsx" | "csv";
    filters?: Record<string, any>;
  }): Promise<APIResponse<{ report_id: string }>> => {
    return apiClient.post("/api/reports/generate", params);
  },

  getReportStatus: (
    reportId: string,
  ): Promise<
    APIResponse<{
      status: "pending" | "processing" | "completed" | "failed";
      progress: number;
      download_url?: string;
      error?: string;
    }>
  > => {
    return apiClient.get(`/api/reports/${reportId}/status`);
  },

  downloadReport: (reportId: string): Promise<void> => {
    return apiClient.download(`/api/reports/${reportId}/download`);
  },

  getReportHistory: (): Promise<
    APIResponse<{
      reports: Array<{
        id: string;
        type: string;
        status: string;
        created_at: string;
        completed_at?: string;
        download_url?: string;
      }>;
    }>
  > => {
    return apiClient.get("/api/reports/history");
  },
};

// Camera Audits endpoints
export const cameraAuditsAPI = {
  getSummary: (
    params: AnalyticsQueryParams,
  ): Promise<
    APIResponse<{
      period: { start: string; end: string };
      total_cameras: number;
      cameras_with_issues: number;
      total_frequent_parker_misses: number;
      total_one_way_plates: number;
      orphan_concentration: string;
      orphan_concentration_detail: string;
      top_insight: string;
      total_orphans_in_period: number;
      unique_orphan_plates: number;
    }>
  > => {
    return apiClient.get("/api/camera-audits/summary", params);
  },

  getCameraHealth: (
    params: AnalyticsQueryParams & { min_misses?: number },
  ): Promise<
    APIResponse<
      Array<{
        camera_id: string;
        zone: string;
        camera_type: string;
        total_misses: number;
        unique_plates_affected: number;
        weekly_trend: Array<{
          week: string;
          misses: number;
          plates_affected: number;
        }>;
        status: string;
        change_detected: string | null;
        interpretation: string;
        example_plates: string[];
      }>
    >
  > => {
    return apiClient.get("/api/camera-audits/cameras", params);
  },

  getOneWayPlates: (
    params: AnalyticsQueryParams & { min_records?: number },
  ): Promise<
    APIResponse<
      Array<{
        zone: string;
        pattern: string;
        unique_plates: number;
        total_records: number;
        interpretation: string;
        example_plates: string[];
      }>
    >
  > => {
    return apiClient.get("/api/camera-audits/one-way-plates", params);
  },

  getCameraDetail: (
    cameraId: string,
    params: AnalyticsQueryParams,
  ): Promise<
    APIResponse<{
      camera_id: string;
      zone: string;
      period: { start: string; end: string };
      daily_trend: Array<{
        date: string;
        exit_misses: number;
        entry_misses: number;
        total_misses: number;
        sample_plates: string[];
      }>;
      total_exit_misses: number;
      total_entry_misses: number;
    }>
  > => {
    return apiClient.get(
      `/api/camera-audits/camera/${cameraId}/detail`,
      params,
    );
  },
};

// Insights endpoints
export const insightsAPI = {
  getInsights: (
    params?: InsightQueryParams,
  ): Promise<APIResponse<Insight[]>> => {
    return apiClient.get("/api/insights", params);
  },

  getSummary: (params?: {
    location_id?: string;
    days?: number;
  }): Promise<APIResponse<InsightSummary>> => {
    return apiClient.get("/api/insights/summary", params);
  },

  generate: (
    request?: GenerateInsightsRequest,
  ): Promise<
    APIResponse<{
      start_date: string;
      end_date: string;
      location_id: string | null;
      locations_found: string[];
      insights_generated: number;
      insights_saved: number;
      insights: Array<{
        category: string;
        severity: string;
        title: string;
        location_id: string | null;
      }>;
    }>
  > => {
    return apiClient.post("/api/insights/generate", request || {});
  },

  markRead: (
    insightId: number,
  ): Promise<APIResponse<{ id: number; is_read: boolean }>> => {
    return apiClient.put(`/api/insights/${insightId}/read`);
  },

  dismiss: (
    insightId: number,
  ): Promise<APIResponse<{ id: number; dismissed: boolean }>> => {
    return apiClient.put(`/api/insights/${insightId}/dismiss`);
  },
};
